import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Form, Select, Tag } from '@arco-design/web-react';

import { syncSelectOptionMeta, useSyncOptionMetaField } from '../../platform/option-meta';
import { DiskPricingVendorDeviceFields, isDiskPricingActive } from '../vendorDevice';
import { useStorageClassOptions } from './hooks/useStorageClassOptions';
import type { StorageClassFormFieldProps } from './types';
import { buildStorageClassSelectExtra } from './utils';

/** 存储类表单项：非计价展示存储类下拉；计价展示厂商设备，提交值回填 deviceID 到 storageClass */
export function StorageClassFormField({
  form,
  field,
  enabled = true,
  billModelId,
  deploymentTypeId,
  resourceCode,
  getPopupContainer,
  formItemClassName,
  requiredMessage,
  placeholder,
  filterProvisioner,
  disabled,
}: StorageClassFormFieldProps) {
  const { t } = useTranslation();
  const pricingCtx = { billModelId, deploymentTypeId, resourceCode };
  const pricingActive = isDiskPricingActive(pricingCtx);
  /** 只读 / 禁用时不请求存储类列表（fixed 下单等） */
  const fetchEnabled = enabled && !pricingActive && !disabled;

  const { options: storageClassOptions, isLoading: storageClassLoading } = useStorageClassOptions(fetchEnabled, filterProvisioner);

  const storageClassSelectOptions = useMemo(
    () =>
      storageClassOptions.map((opt) => ({
        value: opt.value,
        extra: buildStorageClassSelectExtra(opt),
        label: (
          <div>
            <span style={{ display: 'inline-block' }}>{opt.label}</span>
            <span style={{ color: 'var(--color-text-3)', marginLeft: 10 }}>{opt.description}</span>
            {opt.provisioner ? (
              <Tag color="orange" style={{ marginLeft: 10 }} size="small">
                {opt.provisioner}
              </Tag>
            ) : null}
          </div>
        ),
      })),
    [storageClassOptions],
  );

  useSyncOptionMetaField(form, { field, listItems: storageClassOptions, enabled: fetchEnabled });

  return (
    <>
      {pricingActive ? (
        <DiskPricingVendorDeviceFields
          form={form}
          billModelId={pricingCtx.billModelId}
          deploymentTypeId={pricingCtx.deploymentTypeId}
          resourceCode={pricingCtx.resourceCode}
          storageClassField={field}
          disabled={disabled}
        />
      ) : null}
      <Form.Item
        field={field}
        label={t('common:storage.class')}
        rules={pricingActive ? undefined : [{ required: true, message: requiredMessage ?? t('common:validate.not-empty') }]}
        hidden={pricingActive}
        className={formItemClassName}
        disabled={disabled}
      >
        <Select
          options={storageClassSelectOptions}
          showSearch
          disabled={disabled}
          placeholder={placeholder ?? t('common:please.select.storage.class')}
          loading={storageClassLoading}
          getPopupContainer={getPopupContainer}
          onChange={(value, option) => {
            syncSelectOptionMeta(form, value, option, storageClassOptions);
          }}
        />
      </Form.Item>
    </>
  );
}
